/* ----------------- React --------------- */
import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
} from "react";

/* ----------------- Third Party Libraries --------------- */
import type { BottomSheetModal } from "@gorhom/bottom-sheet";

/* ----------------- Hooks --------------- */
import type { Media, Professor, SkillTypes, Slide } from "@memorang/types";
import BottomSheetModalWrapper from "@memorang/ui/components/BottomSheetModalWrapper";
import Box from "@memorang/ui/components/Box";
import Button from "@memorang/ui/components/Button";
import ImagesCarousel from "@memorang/ui/components/ImagesCarousel";
import SheetHandle from "@memorang/ui/components/SheetHandle";
import InfoAlert from "components/InfoAlert";
import { useTranslation } from "react-i18next";
import type {
	NativeScrollEvent,
	NativeSyntheticEvent,
	Text as TextRef,
} from "react-native";
import { ScrollView } from "react-native";
import { Divider, Text } from "react-native-paper";
import AudioPlayerContainer from "../containers/AudioPlayerContainer";
import DiscussionView from "./DiscussionView";
import ItemStem from "./ItemStem";
import type { SlideData } from "./SlideShow";
import SlideShowRenderer from "./SlideShowRenderer";
import StimulusInfo from "./StimulusInfo";
import StimulusTabContent from "./StimulusTabContent";

type Props = {
	showModal?: boolean;
	toggleShowModal: () => void;
	parentStem?: string;
	parentInstruction?: string;
	parentAudio?: string;
	parentTitle?: string;
	professor?: Professor;
	handleClickInsertableText?: (choiceId: string) => void;
	mode?: "sheet" | "modal";
	parentIndex?: number;
	slideshowData?: SlideData[];
	skill?: SkillTypes;
	isSlidesHasPassage?: boolean;
	slides?: Slide[];
	insertText?: string | null;
	disableInsertableText?: boolean;
	itemId?: number;
	parentImages?: Media[];
};

const PassageStemModal = forwardRef(
	(
		{
			showModal,
			toggleShowModal,
			parentStem,
			parentInstruction,
			parentTitle,
			handleClickInsertableText,
			parentIndex,
			slideshowData,
			professor,
			skill,
			slides,
			isSlidesHasPassage,
			insertText,
			disableInsertableText,
			itemId,
			parentAudio,
			parentImages,
		}: Props,
		ref,
	) => {
		const { t } = useTranslation();
		const snapPoints = useMemo(() => ["10%", "100%"], []);
		const lastScrollPosition = useRef(0);
		const shouldScrollToHighlight = useRef(false);

		const bottomSheetModalRef = useRef<BottomSheetModal>(null);
		const hasBothParentAudioAndStem =
			slideshowData && slideshowData.length > 0 && parentStem;

		const bottomSheetScrollViewRef = useRef<ScrollView>(null);
		const highlightedTextRef = useRef<TextRef>(null);

		useImperativeHandle(ref, () => ({
			scrollToHighlightedText,
		}));

		useEffect(() => {
			if (showModal) {
				bottomSheetModalRef.current?.present();
			}
		}, [showModal]);

		const scrollToHighlightedText = useCallback(() => {
			shouldScrollToHighlight.current = true;
			const highlightedText = highlightedTextRef.current;
			if (highlightedText) {
				highlightedText.measureLayout(
					bottomSheetScrollViewRef.current?.getScrollableNode(),
					(_, y) => {
						bottomSheetScrollViewRef.current?.scrollTo({
							y,
							animated: true,
						});
						lastScrollPosition.current = y;
					},
				);
			}
		}, []);

		const handleSheetChanges = useCallback(
			(index: number) => {
				if (index === -1) {
					toggleShowModal();
					lastScrollPosition.current = 0;
					shouldScrollToHighlight.current = false;
					return;
				}

				if (index === 1 && shouldScrollToHighlight.current) {
					scrollToHighlightedText();
				}
			},
			[toggleShowModal, scrollToHighlightedText],
		);

		const ReadView = () => {
			return (
				<Box gap={16}>
					<Text variant="titleLarge">{parentTitle}</Text>
					{insertText && (
						<Box gap={16}>
							<Divider />
							<InfoAlert icon="text" text={insertText} />
							<Divider />
						</Box>
					)}
					{parentStem && (
						<ItemStem
							text={parentStem}
							fontSize={14}
							handleClickInsertableText={handleClickInsertableText}
							highlightedTextRef={highlightedTextRef}
							disableInsertableText={disableInsertableText}
							customMarkdownStyles={{
								paragraph: {
									marginVertical: 10,
								},
							}}
						/>
					)}
					{!hasBothParentAudioAndStem && (
						<SlideShowRenderer slideshowData={slideshowData} itemId={itemId} />
					)}
					{parentAudio && (
						<AudioPlayerContainer uri={parentAudio} itemId={itemId} />
					)}
					{parentImages && <ImagesCarousel images={parentImages} />}
				</Box>
			);
		};

		const NonTabbedbView = () => {
			return (
				<ScrollView
					ref={bottomSheetScrollViewRef}
					contentContainerStyle={{
						paddingBottom: 100,
						gap: 16,
					}}
					onScroll={onScroll}
					scrollEventThrottle={20}
					contentOffset={{ x: 0, y: lastScrollPosition.current }}
				>
					{parentInstruction && parentIndex === 1 && (
						<InfoAlert text={parentInstruction} />
					)}
					{professor ? (
						<DiscussionView professor={professor} title={parentTitle!} />
					) : (
						<ReadView />
					)}
				</ScrollView>
			);
		};

		const Header = () => {
			return (
				<Box paddingBottom={16}>
					<SheetHandle onPress={toggleShowModal} />
					<Box
						flexDirection="row"
						justifyContent="space-between"
						alignItems="center"
						paddingHorizontal={16}
					>
						{skill ? <StimulusInfo skill={skill} /> : <Box />}
						<Button mode="contained" onPress={toggleShowModal}>
							{t("common.continue")}
						</Button>
					</Box>
				</Box>
			);
		};

		const onScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
			lastScrollPosition.current = event.nativeEvent.contentOffset.y;
			shouldScrollToHighlight.current = false;
		};

		return (
			<BottomSheetModalWrapper
				showModal={showModal}
				snapPoints={snapPoints}
				handleSetSnapIndex={handleSheetChanges}
				ref={bottomSheetModalRef}
				snapIndex={1}
				customHandle={Header}
				animateOnMount
				enableDismissOnClose
				enablePanDownToClose
				backdropPressBehavior="collapse"
			>
				{isSlidesHasPassage && slides && slides.length > 0 ? (
					<StimulusTabContent
						parentTitle={parentTitle!}
						slideshowData={slideshowData!}
						parentInstruction={parentInstruction!}
						parentIndex={parentIndex!}
						slides={slides!}
						itemId={itemId}
					/>
				) : (
					<NonTabbedbView />
				)}
			</BottomSheetModalWrapper>
		);
	},
);

export default PassageStemModal;
